import { useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { bibleState } from '@/state/bible-state';
import { GuideStepCard } from './guide-step-card';
import { GuideNav } from './guide-nav';

/** Pause before a completed gate advances the tour — long enough to read
 *  as acknowledgment, short enough to feel responsive. */
const AUTO_ADVANCE_MS = 800;

/**
 * Guide tab content — shows the current tour step with navigation.
 * Manages highlight effects on target elements.
 */
export const GuideContent = observer(function GuideContent() {
  const step = bibleState.guideStep;
  const currentStep = bibleState.guideCurrentStep;
  const totalSteps = bibleState.guideTotalSteps;
  const highlightSelector = bibleState.guideHighlightSelector;
  const isReady = bibleState.isGuideStepReady;

  // Auto-advance a gated step once its condition flips from unmet to met
  // ("create a group" → group created → next slide). Arriving on an
  // already-satisfied step (Previous over completed work) stays put: the
  // gate must be SEEN unmet on this step before completion advances it.
  const hasGate = !!step?.isReady;
  const sawUnreadyRef = useRef(false);
  useEffect(() => {
    sawUnreadyRef.current = false;
  }, [currentStep]);
  useEffect(() => {
    if (!hasGate) return;
    if (!isReady) {
      sawUnreadyRef.current = true;
      return;
    }
    if (!sawUnreadyRef.current) return;
    const timer = setTimeout(() => bibleState.guideNextStep(), AUTO_ADVANCE_MS);
    return () => clearTimeout(timer);
  }, [hasGate, isReady, currentStep]);

  // Apply/remove highlight class on target element
  useEffect(() => {
    if (!highlightSelector) return;

    const el = document.querySelector(highlightSelector);
    if (el) {
      el.classList.add('guide-highlight');
    }

    return () => {
      if (el) {
        el.classList.remove('guide-highlight');
      }
    };
  }, [highlightSelector, currentStep]);

  // Auto-start on tab open intentionally disabled. With no active step the
  // component returns null below — the tab will need its own intro/empty
  // state once the help UX is redesigned. Re-enable alongside that work.

  if (!step) return null;

  const handlePrev = () => bibleState.guidePrevStep();
  const handleNext = () => bibleState.guideNextStep();

  const handleDone = () => {
    bibleState.endGuide();
    bibleState.hideUserDataPanel();
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 space-y-4">
        {/* Progress bar */}
        <div className="h-1 overflow-hidden rounded-full bg-stone-200 dark:bg-stone-700">
          <div
            className="h-full rounded-full bg-cyan-500 transition-all duration-300"
            style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
          />
        </div>

        <GuideStepCard
          stepNumber={currentStep}
          totalSteps={totalSteps}
          title={step.title}
          description={step.description}
          nav={
            <GuideNav
              stepNumber={currentStep}
              totalSteps={totalSteps}
              isReady={isReady}
              onPrev={handlePrev}
              onNext={handleNext}
              onDone={handleDone}
            />
          }
        />

        {/* Privacy & Support links on welcome step */}
        {currentStep === 0 && (
          <div className="flex items-center justify-center gap-4 pt-4">
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-stone-400 transition-colors hover:text-stone-600 dark:text-stone-500 dark:hover:text-stone-300"
            >
              Privacy
            </a>
            <span className="text-stone-300 dark:text-stone-600">·</span>
            <a
              href="/support"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-stone-400 transition-colors hover:text-stone-600 dark:text-stone-500 dark:hover:text-stone-300"
            >
              Support
            </a>
          </div>
        )}
      </div>
    </div>
  );
});
